import { useState, Suspense } from 'react'
import { Drawer, DrawerContent, DrawerHeader, DrawerFooter, DrawerTitle } from '@/components/ui/drawer'
import { FixedMobileBottomBar } from '@/components/ui/FixedMobileBottomBar'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useUnitDiscountQuery } from '@/api/accommodation/hooks'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { LocationInquirySheet } from '../inquiry/LocationInquirySheet'
import { LocationWatchmanSheet } from '../location-watchman/LocationWatchmanSheet'
import { PriceGuaranteePanel } from '../price-guarantee/PriceGuaranteePanel'
import { RequestBookingPanel } from './RequestBookingPanel'
import { DateSelectionCalendar } from './DateSelectionCalendar'
import { DateSelectionSummary } from './DateSelectionSummary'
import { DateSelectionBottomBar, DateSelectionBottomBarSkeleton } from './DateSelectionBottomBar'
import { LastMinuteOfferBanner } from './LastMinuteOfferBanner'
import { DateSelectionCalendarSkeleton } from './DateSelectionCalendarSkeleton'
import { DateSelectionSummarySkeleton } from './DateSelectionSummarySkeleton'

interface DateSelectionMobileProps {
  accommodation: AccommodationDetail
}

export const DateSelectionMobile = ({ accommodation }: DateSelectionMobileProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [guaranteeOpen, setGuaranteeOpen] = useState(false)
  const { discount } = useUnitDiscountQuery(accommodation.unit.id)

  const openSheet = (withGuarantee: boolean) => {
    setGuaranteeOpen(withGuarantee)
    setIsOpen(true)
  }

  return (
    <>
      {/* Mobile bottom bar */}
      <FixedMobileBottomBar>
        <ErrorBoundary title="Chyba při načítání" message="Nepodařilo se načíst informace o ceně.">
          <Suspense fallback={<DateSelectionBottomBarSkeleton />}>
            <DateSelectionBottomBar
              accommodation={accommodation}
              onOpenSheet={() => openSheet(false)}
              onOpenGuarantee={() => openSheet(true)}
            />
          </Suspense>
        </ErrorBoundary>
      </FixedMobileBottomBar>

      {/* Date selection drawer */}
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent className="max-h-[90dvh]">
          <DrawerHeader className="pb-2">
            {discount ? (
              <>
                <DrawerTitle className="sr-only">Volné termíny</DrawerTitle>
                <Suspense fallback={null}>
                  <LastMinuteOfferBanner accommodation={accommodation} />
                </Suspense>
              </>
            ) : (
              <DrawerTitle>Volné termíny</DrawerTitle>
            )}
          </DrawerHeader>

          <ErrorBoundary title="Chyba při načítání kalendáře" message="Nepodařilo se načíst dostupnost termínů.">
            <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
              <Suspense fallback={<DateSelectionCalendarSkeleton />}>
                <DateSelectionCalendar accommodation={accommodation} />
              </Suspense>
            </div>

            <DrawerFooter className="shrink-0 gap-1.5 border-t border-slate-100 p-3">
              <div className="flex w-full flex-1 items-center justify-between gap-4">
                <Suspense fallback={<DateSelectionSummarySkeleton />}>
                  <DateSelectionSummary accommodation={accommodation} />
                </Suspense>
              </div>
              <RequestBookingPanel accommodation={accommodation} />
              <PriceGuaranteePanel accommodation={accommodation} defaultOpen={guaranteeOpen} />
              {/* Skipped when there is no availability at all — the calendar's
                  empty state already carries its own watchman CTA. */}
              {discount && <LocationWatchmanSheet accommodation={accommodation} variant="panel" />}
              <LocationInquirySheet accommodation={accommodation} />
            </DrawerFooter>
          </ErrorBoundary>
        </DrawerContent>
      </Drawer>
    </>
  )
}
